'use client';

import { useEffect, useId, useRef, useState } from 'react';

import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Sparkles,
  X,
} from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';

import { cn } from '@ci/ui/utils';

import type { CelebrationPlan } from '~/lib/release-celebrations/celebration-variety';
import type { ReleaseNoteEntry } from '~/lib/release-celebrations/release-note-history';

import { CelebrationBurst } from './celebration-burst';

/**
 * PAY-4392: the update notice. Presentational: the controller owns the claim,
 * the announcement and the preference write.
 *
 * The card sits in the centre and stays until it is dismissed. It never
 * takes focus. Clicks outside it still reach the page. The celebration, when
 * there is one, covers the viewport behind this card and then leaves; the
 * card does not. The verse is plain text with its own line breaks (PAY-4474),
 * upright, in the foreground colour: italic grey was hard to read (PAY-4683).
 * A release with no verse says "Performance enhancements and fixes." The footer links to the changelog (PAY-4490) for the full
 * detail, in a new tab so the card and the page behind it stay put.
 * `plan` is null under reduced motion, and then nothing moves at all (the
 * card itself does not animate in either).
 *
 * PAY-4612: `entries` are the release notes oldest first, and the card opens
 * on `startIndex`. With more than one, previous and next arrows (and the
 * left and right arrow keys inside the card) step through them. `mode` says
 * how the card was opened: `release` for a newly claimed release, `history`
 * for the profile menu's "What's new", which has no celebration, takes focus
 * (the menu that opened it has just closed) and offers no "hide" link.
 */
export function UpdateNotice({
  entries,
  startIndex,
  mode = 'release',
  loading = false,
  plan,
  hiding,
  onDismiss,
  onHideFuture,
  onCelebrationDone,
}: {
  entries: ReleaseNoteEntry[];
  startIndex: number;
  mode?: 'release' | 'history';
  /** History still loading; shown only while there is nothing to page. */
  loading?: boolean;
  plan: CelebrationPlan | null;
  hiding: boolean;
  onDismiss: () => void;
  onHideFuture: () => void;
  /** When set, a finished celebration starts another instead of leaving. */
  onCelebrationDone?: () => void;
}) {
  const t = useTranslations('common.updateNotice');
  const format = useFormatter();
  const titleId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const [shownPlan, setShownPlan] = useState(plan);
  const [bursting, setBursting] = useState(plan !== null);

  if (plan !== shownPlan) {
    setShownPlan(plan);
    setBursting(plan !== null);
  }

  // The current page, kept by release id so a history that loads after the
  // card opened (and shifts every index) does not move the reader.
  const [selectedId, setSelectedId] = useState<string | null>(
    entries[startIndex]?.releaseId ?? null,
  );
  const [prevStartId, setPrevStartId] = useState(
    entries[startIndex]?.releaseId ?? null,
  );
  const startId = entries[startIndex]?.releaseId ?? null;
  if (startId !== prevStartId) {
    setPrevStartId(startId);
    if (selectedId === null || selectedId === prevStartId) {
      setSelectedId(startId);
    }
  }
  const found = entries.findIndex((entry) => entry.releaseId === selectedId);
  const index = found === -1 ? Math.max(0, startIndex) : found;
  const entry = entries[index] ?? null;
  const canGoOlder = index > 0;
  const canGoNewer = index < entries.length - 1;
  const publishedOnFor = (target: ReleaseNoteEntry | null) =>
    target?.publishedAt
      ? format.dateTime(new Date(target.publishedAt), { dateStyle: 'medium' })
      : null;
  const publishedOn = publishedOnFor(entry);

  // Read out what a page change or a finished load puts on the card. The
  // region is mounted empty with the card and filled only by those, so the
  // card's first appearance is not announced twice (the controller announces
  // a claimed release).
  const [announcement, setAnnouncement] = useState('');
  const describe = (target: ReleaseNoteEntry) =>
    [target.releaseNumber, publishedOnFor(target), target.poem || t('noVerse')]
      .filter(Boolean)
      .join('. ');
  const [loadAnnounced, setLoadAnnounced] = useState(false);
  if (mode === 'history' && !loading && !loadAnnounced) {
    setLoadAnnounced(true);
    setAnnouncement(entry ? describe(entry) : t('empty'));
  }

  const goTo = (next: number) => {
    const target = entries[next];
    if (!target) return;
    setSelectedId(target.releaseId);
    setAnnouncement(
      `${t('position', { current: next + 1, total: entries.length })}. ${describe(target)}`,
    );
  };

  // Opened from a menu, the card takes focus, and closing it gives focus back
  // to whatever held it before (the menu's trigger, once the menu has closed).
  const returnFocusTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (mode !== 'history') return;
    const previous = document.activeElement;
    if (previous instanceof HTMLElement && previous !== document.body) {
      returnFocusTo.current = previous;
    }
    sectionRef.current?.focus();
  }, [mode]);

  const dismiss = () => {
    const target = returnFocusTo.current;
    onDismiss();
    if (target?.isConnected) target.focus();
  };

  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-6">
        <section
          ref={sectionRef}
          aria-labelledby={titleId}
          data-test="update-notice"
          data-mode={mode}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              dismiss();
            } else if (event.key === 'ArrowLeft' && canGoOlder) {
              event.preventDefault();
              goTo(index - 1);
            } else if (event.key === 'ArrowRight' && canGoNewer) {
              event.preventDefault();
              goTo(index + 1);
            }
          }}
          // No CSS entrance: every style brings the card in itself (PAY-4631),
          // and a CSS one would fight its transform.
          className="bg-popover text-popover-foreground pointer-events-auto w-[min(26rem,100%)] rounded-[20px] border p-5 shadow-[0_24px_70px_rgba(0,0,0,0.2)] focus:outline-none"
        >
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            data-test="update-notice-page-announcement"
          >
            {announcement}
          </div>
          <div className="flex items-start gap-3">
            <span className="bg-primary/10 text-primary relative flex size-8 shrink-0 items-center justify-center rounded-full">
              <Sparkles className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-base leading-6 font-medium">
                {mode === 'history' ? t('whatsNewTitle') : t('title')}
              </h2>
              {entry?.releaseNumber || publishedOn ? (
                <p
                  className="text-muted-foreground text-xs tabular-nums"
                  data-test="update-notice-release"
                >
                  {[entry?.releaseNumber, publishedOn]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label={t('dismiss')}
              data-test="update-notice-dismiss"
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -mt-1 -mr-1 rounded-sm p-1 focus-visible:ring-2 focus-visible:outline-none"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>

          {/* The verse spans the card's full width, not the title column, so
              a busy release's long lines (up to 52 characters) do not wrap. */}
          {entry?.poem ? (
            <p
              key={entry.releaseId}
              className="text-foreground animate-in fade-in mt-4 text-sm leading-relaxed whitespace-pre-line duration-300 motion-reduce:animate-none"
              data-test="update-notice-poem"
            >
              {entry.poem}
            </p>
          ) : null}

          {/* PAY-4683: a release with nothing customer-facing has no verse.
              Say so, rather than leave the card with an empty body. */}
          {entry && !entry.poem ? (
            <p
              key={entry.releaseId}
              className="text-foreground mt-4 text-sm leading-relaxed"
              data-test="update-notice-no-verse"
            >
              {t('noVerse')}
            </p>
          ) : null}

          {!entry && mode === 'history' ? (
            <p
              className="text-muted-foreground mt-4 text-sm"
              data-test="update-notice-empty"
            >
              {loading ? t('loading') : t('empty')}
            </p>
          ) : null}

          {entries.length > 1 ? (
            <nav
              aria-label={t('historyLabel')}
              className="mt-4 flex items-center justify-between gap-2"
              data-test="update-notice-pager"
            >
              <PagerButton
                label={t('older')}
                disabled={!canGoOlder}
                onClick={() => goTo(index - 1)}
                dataTest="update-notice-older"
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
              </PagerButton>
              <span
                className="text-muted-foreground text-xs tabular-nums"
                data-test="update-notice-position"
              >
                {t('position', { current: index + 1, total: entries.length })}
              </span>
              <PagerButton
                label={t('newer')}
                disabled={!canGoNewer}
                onClick={() => goTo(index + 1)}
                dataTest="update-notice-newer"
              >
                <ChevronRight className="size-4" aria-hidden="true" />
              </PagerButton>
            </nav>
          ) : null}

          <div
            className={cn(
              'border-border/60 mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-3',
              mode === 'history' ? 'justify-end' : 'justify-between',
            )}
          >
            {mode === 'release' ? (
              <button
                type="button"
                onClick={onHideFuture}
                disabled={hiding}
                data-test="update-notice-hide-future"
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm text-xs underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
              >
                {t('hideFuture')}
              </button>
            ) : null}
            <a
              href="/changelog"
              target="_blank"
              rel="noopener noreferrer"
              data-test="update-notice-changelog"
              className="text-primary focus-visible:ring-ring inline-flex items-center gap-1 rounded-sm text-xs font-medium underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
            >
              {t('changelog')}
              <ExternalLink className="size-3" aria-hidden="true" />
              <span className="sr-only">{t('opensInNewTab')}</span>
            </a>
          </div>
        </section>
      </div>
      {/* After the card on purpose: refs attach in tree order, so the card's
          ref is set by the time this layout effect poses it (PAY-4631). It
          paints underneath (z-40 against the card's z-50), except for the
          styles that draw over the card (DRAWS_OVER_CARD, PAY-4644). */}
      {plan && bursting ? (
        <CelebrationBurst
          plan={plan}
          cardRef={sectionRef}
          onDone={() => {
            if (onCelebrationDone) onCelebrationDone();
            else setBursting(false);
          }}
        />
      ) : null}
    </>
  );
}

function PagerButton({
  label,
  disabled,
  onClick,
  dataTest,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  dataTest: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      data-test={dataTest}
      className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring flex size-7 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-30"
    >
      {children}
    </button>
  );
}
